// Phase-6 plots showcase. Lives at /dev/plots. Auth-independent.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Brandmark, Button, SlashLabel } from "@/components/primitives";
import { RadarPlot, ScatterPlot } from "@/components/Plots";
import {
  NEIGHBOR_FLAVORS,
  SCATTER_POINTS,
  SAMPLE_TARGET,
  SMELL_AXES,
  TASTE_AXES,
} from "@/services/mock";

// Synthesize a "current" sensory profile by jittering the target — purely
// for the visual showcase (real loss/cost lands in phase 7).
function jitter(values: number[], seed: number): number[] {
  return values.map((v, i) => {
    const noise = Math.sin((seed + i) * 0.7) * 0.6 + Math.cos(i * 1.3) * 0.4;
    return Math.max(0, Math.min(10, v * 0.85 + noise));
  });
}

export function DevPlots() {
  const [hoveredTaste, setHoveredTaste] = useState<number | null>(null);
  const [hoveredSmell, setHoveredSmell] = useState<number | null>(null);

  const currentTaste = useMemo(() => jitter(SAMPLE_TARGET.taste, 7), []);
  const currentSmell = useMemo(() => jitter(SAMPLE_TARGET.smell, 19), []);

  return (
    <div
      style={{
        padding: "32px 40px",
        maxWidth: 1100,
        margin: "0 auto",
        background: "var(--bg)",
        minHeight: "100vh",
      }}
    >
      <div className="hstack" style={{ justifyContent: "space-between", marginBottom: 24 }}>
        <Brandmark />
        <SlashLabel>Phase 6 — Plots showcase</SlashLabel>
      </div>

      <div className="hstack gap-3" style={{ marginBottom: 24 }}>
        <Link to="/dev/showcase">
          <Button variant="ghost">/dev/showcase</Button>
        </Link>
        <Link to="/flavors/w1">
          <Button variant="ghost">/flavors/w1</Button>
        </Link>
      </div>

      <Section title="Scatter — flavor-space embedding">
        <div
          className="panel"
          style={{
            padding: 8,
            background: "var(--bg-sunk)",
            borderRadius: 10,
            display: "inline-block",
          }}
        >
          <ScatterPlot
            neighbors={NEIGHBOR_FLAVORS}
            target={SCATTER_POINTS.target}
            current={SCATTER_POINTS.current}
            width={520}
            height={380}
            targetName={SAMPLE_TARGET.name}
            currentName="Working flask"
          />
        </div>
        <p
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--ink-4)",
            maxWidth: 620,
            marginTop: 12,
          }}
        >
          Crosshair is the target; accent-tinted disk is the current flask.
          Dashed line connects them. The 3 nearest neighbors get always-on
          leader-line labels; hover any point for a tooltip with family chip.
        </p>
      </Section>

      <Section title="Radar — Taste · 9 axes">
        <div
          className="panel"
          style={{
            padding: "12px 4px 14px",
            background: "var(--bg-sunk)",
            borderRadius: 10,
            display: "inline-block",
          }}
        >
          <RadarPlot
            axes={TASTE_AXES}
            target={SAMPLE_TARGET.taste}
            current={currentTaste}
            size={320}
            label="Taste · 9 axes"
            hovered={hoveredTaste}
            onHover={setHoveredTaste}
          />
        </div>
      </Section>

      <Section title="Radar — Smell · 20 axes">
        <div
          className="panel"
          style={{
            padding: "12px 4px 14px",
            background: "var(--bg-sunk)",
            borderRadius: 10,
            display: "inline-block",
          }}
        >
          <RadarPlot
            axes={SMELL_AXES}
            target={SAMPLE_TARGET.smell}
            current={currentSmell}
            size={360}
            label="Smell · 20 axes"
            hovered={hoveredSmell}
            onHover={setHoveredSmell}
          />
        </div>
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: 28 }}>
      <h2
        className="display"
        style={{ fontSize: 14, marginBottom: 12, color: "var(--ink-2)" }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}
